import type { Logger as PinoLogger } from 'pino';
import { createLogger as logging_createLogger } from '@understory-io/logging';
import { createContext } from 'react-router';
import { LogLevel } from '~/configuration';

export function createLogger() {
  return logging_createLogger({
    browser: typeof window !== 'undefined',
    level: LogLevel,
  });
}

export type Logger = PinoLogger;

export const loggerContext = createContext<Logger>();

const levelRank = { debug: 0, info: 1, warn: 2, error: 3 } as const;

type ClientLogLevel = keyof typeof levelRank;

type LogFn = (objOrMsg: unknown, msg?: string) => void;

export type ClientLogger = Record<ClientLogLevel, LogFn>;

export function createConsoleLogger(level: ClientLogLevel): ClientLogger {
  // LOG_LEVEL comes from the build env unchecked; pino's default beats
  // silencing or flooding the console over a typo.
  const threshold = levelRank[level] ?? levelRank.info;
  const method = (name: ClientLogLevel): LogFn =>
    levelRank[name] < threshold
      ? () => {}
      : (objOrMsg, msg) =>
          msg === undefined
            ? console[name](objOrMsg)
            : console[name](msg, objOrMsg);

  return {
    debug: method('debug'),
    info: method('info'),
    warn: method('warn'),
    error: method('error'),
  };
}

// Browser logs only ever reach the visitor's console, so the client build
// keeps pino out of every page's initial JS. Server renders still log
// through pino, in the same JSON shape as the rest of the server logs.
export const clientLogger: ClientLogger = import.meta.env.SSR
  ? createLogger()
  : createConsoleLogger(LogLevel);
